import React, { useState, useEffect } from 'react'
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Table,
  Badge,
  Spinner,
  Alert,
  Row,
  Col,
  Input,
  Label
} from 'reactstrap'
import { getReworkMilestonesDetails } from '../../../../helpers/rfq_dashboard_api'

const ReworkMilestonesModal = ({ 
  isOpen, 
  toggle, 
  filters = {},
  title = "Rework Milestones Details"
}) => {
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState([])
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 10,
    total: 0,
    total_pages: 0,
    has_next: false,
    has_prev: false
  })
  const [error, setError] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [entriesPerPage, setEntriesPerPage] = useState(10)

  // Reset pagination and fetch data when modal opens
  useEffect(() => {
    if (isOpen) {
      // Reset pagination to first page when modal opens
      setPagination(prev => ({ ...prev, current_page: 1 }))
      setSearchTerm('')
      fetchData()
    }
  }, [isOpen, entriesPerPage])

  // Fetch data when pagination changes (but not on initial open)
  useEffect(() => {
    if (isOpen && pagination.current_page > 1) {
      fetchData()
    }
  }, [pagination.current_page])

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setPagination({
        current_page: 1,
        per_page: 10,
        total: 0,
        total_pages: 0,
        has_next: false,
        has_prev: false
      })
      setSearchTerm('')
      setData([])
      setError(null)
    }
  }, [isOpen])

  const fetchData = async () => {
    try {
      setLoading(true)
      setError(null)

      const params = {
        ...filters,
        page: pagination.current_page,
        per_page: entriesPerPage
      }

      console.log("Fetching rework milestones details:", params)

      const response = await getReworkMilestonesDetails(params)

      if (response.status) {
        setData(response.data.milestones || [])
        setPagination(response.data.pagination || pagination)
      } else {
        setError(response.message || 'Failed to fetch rework milestones details')
      }
    } catch (error) {
      console.error('Error fetching rework milestones details:', error)
      setError(error.message || 'An error occurred while fetching data')
    } finally {
      setLoading(false)
    }
  }

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, current_page: newPage }))
  }

  const handleEntriesPerPageChange = (newEntriesPerPage) => {
    setEntriesPerPage(newEntriesPerPage)
    setPagination(prev => ({
      ...prev,
      current_page: 1, // Reset to first page when changing entries per page
      per_page: newEntriesPerPage
    }))
  }

  const formatCurrency = (amount) => {
    if (!amount) return '₹0.00'
    return `₹${parseFloat(amount).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
  }

  const formatDate = (dateString) => {
    if (!dateString) return '-'
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    })
  }

  const formatReworkDate = (dateString) => {
    if (!dateString) return '-'
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit'
    })
  }

  const getStatusBadgeColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'rework': return 'warning'
      case 'completed': return 'success'
      case 'in_progress': return 'info'
      case 'pending': return 'secondary'
      default: return 'secondary'
    }
  }

  // Filter data based on search term
  const filteredData = data.filter(milestone =>
    milestone.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    milestone.rfq_code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    milestone.rfq_title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    milestone.project_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    milestone.vendor_name?.toLowerCase().includes(searchTerm.toLowerCase())
  )

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="xl" className="rfq-modal">
      <ModalHeader toggle={toggle}>
        <i className="bx bx-error-circle me-2 text-warning"></i>
        {title}
        <small className="text-muted ms-2">
          ({pagination.total} milestones in rework)
        </small>
      </ModalHeader>
      
      <ModalBody>
        {error && (
          <Alert color="danger" className="mb-3">
            <i className="bx bx-error-circle me-2"></i>
            {error}
          </Alert>
        )}

        {/* Search and Entries Controls */}
        <Row className="mb-3">
          <Col md={6}>
            <div className="search-box">
              <div className="position-relative">
                <Input
                  type="text"
                  id="search"
                  placeholder="Search by milestone title, RFQ, project, or vendor..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <i className="bx bx-search-alt search-icon"></i>
              </div>
            </div>
          </Col>
          <Col md={2}></Col>
          <Col md={2}>
            <div className="d-flex align-items-center">
              <span className="me-2 text-nowrap">Show:</span>
              <Input
                type="select"
                id="entriesSelect"
                value={entriesPerPage}
                onChange={(e) => handleEntriesPerPageChange(parseInt(e.target.value))}
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </Input>
            </div>
          </Col>
        </Row>

        {loading ? (
          <div className="text-center py-4">
            <Spinner color="primary" />
            <p className="mt-2">Loading rework milestones...</p>
          </div>
        ) : (
          <>
            {/* Summary Information */}
            <div className="d-flex justify-content-between align-items-center mb-3 p-2 bg-light rounded">
              <div>
                <strong className="text-primary">
                  {pagination.total} Total Rework Milestones
                </strong>
                {searchTerm && (
                  <span className="text-muted ms-2">
                    | {filteredData.length} matching "{searchTerm}"
                  </span>
                )}
              </div>
              <div className="text-muted small">
                <i className="bx bx-info-circle me-1"></i>
                Showing {entriesPerPage} entries per page
              </div>
            </div>

            <div className="table-responsive">
              <Table className="table-nowrap table-hover">
                <thead className="table-light">
                  <tr>
                    <th>RFQ#</th>
                    <th>RFQ Title</th>
                    <th>Project</th>
                    <th>Vendor</th>
                    <th>Milestone Title</th>
                    <th>Milestone Amount</th>
                    <th>Status</th>
                    <th>Created Date</th>
                    <th>Rework Date</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredData.length > 0 ? (
                    filteredData.map((milestone, index) => (
                      <tr key={milestone.id || index}>
                        <td>
                          <strong className="text-primary">{milestone.rfq_code || '-'}</strong>
                        </td>
                        <td>
                          <div className="text-truncate" style={{maxWidth: '200px'}}>
                            {milestone.rfq_title || '-'}
                          </div>
                        </td>
                        <td>{milestone.project_name || '-'}</td>
                        <td>{milestone.vendor_name || 'Not Assigned'}</td>
                        <td>
                          <strong className="text-warning">{milestone.title || '-'}</strong>
                          <div className="text-muted small mt-1">
                            <i className="bx bx-error-circle me-1"></i>
                            Requires rework
                          </div>
                        </td>
                        <td>
                          <strong className="text-success">
                            {formatCurrency(milestone.payment_amount)}
                          </strong>
                        </td>
                        <td>
                          <Badge color={getStatusBadgeColor(milestone.milestone_status)}>
                            <i className="bx bx-error-circle me-1"></i>
                            {milestone.milestone_status || 'Rework'}
                          </Badge>
                        </td>
                        <td>{formatDate(milestone.created_at)}</td>
                        <td>
                          <small className="text-warning">
                            <i className="bx bx-calendar me-1"></i>
                            {formatReworkDate(milestone.updated_at)}
                          </small>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="9" className="text-center py-4">
                        <i className="bx bx-info-circle display-4 text-muted"></i>
                        <p className="text-muted mt-2">
                          {searchTerm ? 'No milestones match your search criteria' : 'No rework milestones found'}
                        </p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>

            {/* Enhanced Pagination */}
            <div className="d-flex justify-content-between align-items-center mt-3">
              <div>
                <small className="text-muted">
                  Showing {filteredData.length > 0 ? ((pagination.current_page - 1) * entriesPerPage) + 1 : 0} to{' '}
                  {Math.min(pagination.current_page * entriesPerPage, pagination.total)} of{' '}
                  {pagination.total} entries
                  {searchTerm && (
                    <span className="text-info ms-2">
                      (filtered from {pagination.total} total entries)
                    </span>
                  )}
                </small>
              </div>

              {pagination.total_pages > 1 && (
                <div className="d-flex align-items-center">
                  {/* First Page Button */}
                  <Button
                    color="outline-secondary"
                    size="sm"
                    onClick={() => handlePageChange(1)}
                    disabled={pagination.current_page === 1}
                    className="me-1"
                    title="First Page"
                  >
                    <i className="bx bx-chevrons-left"></i>
                  </Button>

                  {/* Previous Page Button */}
                  <Button
                    color="outline-primary"
                    size="sm"
                    onClick={() => handlePageChange(pagination.current_page - 1)}
                    disabled={!pagination.has_prev}
                    className="me-2"
                    title="Previous Page"
                  >
                    <i className="bx bx-chevron-left me-1"></i>
                    Previous
                  </Button>

                  {/* Page Numbers */}
                  <div className="d-flex align-items-center mx-2">
                    {/* Show page numbers around current page */}
                    {(() => {
                      const currentPage = pagination.current_page
                      const totalPages = pagination.total_pages
                      const pages = []

                      // Calculate start and end page numbers to show
                      let startPage = Math.max(1, currentPage - 2)
                      let endPage = Math.min(totalPages, currentPage + 2)

                      // Adjust if we're near the beginning or end
                      if (endPage - startPage < 4) {
                        if (startPage === 1) {
                          endPage = Math.min(totalPages, startPage + 4)
                        } else if (endPage === totalPages) {
                          startPage = Math.max(1, endPage - 4)
                        }
                      }

                      // Add first page if not included
                      if (startPage > 1) {
                        pages.push(
                          <Button
                            key={1}
                            color={1 === currentPage ? "primary" : "outline-secondary"}
                            size="sm"
                            onClick={() => handlePageChange(1)}
                            className="me-1"
                          >
                            1
                          </Button>
                        )
                        if (startPage > 2) {
                          pages.push(<span key="ellipsis1" className="mx-1">...</span>)
                        }
                      }

                      // Add page numbers
                      for (let i = startPage; i <= endPage; i++) {
                        pages.push(
                          <Button
                            key={i}
                            color={i === currentPage ? "primary" : "outline-secondary"}
                            size="sm"
                            onClick={() => handlePageChange(i)}
                            className="me-1"
                          >
                            {i}
                          </Button>
                        )
                      }

                      // Add last page if not included
                      if (endPage < totalPages) {
                        if (endPage < totalPages - 1) {
                          pages.push(<span key="ellipsis2" className="mx-1">...</span>)
                        }
                        pages.push(
                          <Button
                            key={totalPages}
                            color={totalPages === currentPage ? "primary" : "outline-secondary"}
                            size="sm"
                            onClick={() => handlePageChange(totalPages)}
                            className="me-1"
                          >
                            {totalPages}
                          </Button>
                        )
                      }

                      return pages
                    })()}
                  </div>

                  {/* Next Page Button */}
                  <Button
                    color="outline-primary"
                    size="sm"
                    onClick={() => handlePageChange(pagination.current_page + 1)}
                    disabled={!pagination.has_next}
                    className="ms-2"
                    title="Next Page"
                  >
                    Next
                    <i className="bx bx-chevron-right ms-1"></i>
                  </Button>

                  {/* Last Page Button */}
                  <Button
                    color="outline-secondary"
                    size="sm"
                    onClick={() => handlePageChange(pagination.total_pages)}
                    disabled={pagination.current_page === pagination.total_pages}
                    className="ms-1"
                    title="Last Page"
                  >
                    <i className="bx bx-chevrons-right"></i>
                  </Button>
                </div>
              )}
            </div>
          </>
        )}
      </ModalBody>
      
      <ModalFooter>
        <Button color="secondary" onClick={toggle}>
          Close
        </Button>
      </ModalFooter>
    </Modal>
  )
}

export default ReworkMilestonesModal
